// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { COURSE_ADMIN_COUNTRIES, DEFAULT_COURSE_ADMIN_COUNTRY_CODE } from './countries';

/** The whole catalogue is fetched in one go, so paging is a display concern only. */
export const COURSE_LIST_PAGE_SIZE = 100;

export const ALL_COUNTRIES = 'all';

export type AnalysisFilter = 'all' | 'analysed' | 'notAnalysed';

/**
 * The OSM-imported geometry a course can hold, as the list filters on it. These are the stored
 * rows (source `osm` in geodata), not what OpenStreetMap holds right now — a course matched to
 * OSM but never imported has none of them.
 */
export const OSM_FEATURE_KINDS = ['greens', 'holes', 'bunkers', 'fairways'] as const;

export type OsmFeatureKind = (typeof OSM_FEATURE_KINDS)[number];

export type OsmFeatureCounts = Record<OsmFeatureKind, number>;

/** The subset of a course row the list view filters on. */
export type FilterableCourse = {
  id: string;
  name: string;
  clubName: string;
  country: string;
  analysisState: string | null;
  osmFeatures: OsmFeatureCounts;
};

export type CourseListFilters = {
  query: string;
  /** ISO 3166-1 alpha-2, or ALL_COUNTRIES. */
  countryCode: string;
  analysisFilter: AnalysisFilter;
  /** Kinds the course must have at least one of. Empty means no OSM constraint. */
  osmFeatures: OsmFeatureKind[];
  /** 1-based. */
  page: number;
};

export const DEFAULT_COURSE_LIST_FILTERS: CourseListFilters = {
  query: '',
  countryCode: DEFAULT_COURSE_ADMIN_COUNTRY_CODE,
  analysisFilter: 'all',
  osmFeatures: [],
  page: 1,
};

const normalize = (value: string) => value.trim().toLowerCase();

/**
 * The catalogue stores a club's country inconsistently — the OSM bulk import writes the country
 * *name* ("Sweden"), while clubs created through the club API carry whatever the caller sent,
 * usually the ISO code ("SE"). So a country selection matches either spelling. A club with no
 * country at all is only reachable through "All countries".
 */
export function matchesCountry(course: { country: string }, countryCode: string): boolean {
  if (countryCode === ALL_COUNTRIES) {
    return true;
  }
  const stored = normalize(course.country ?? '');
  if (!stored) {
    return false;
  }
  const country = COURSE_ADMIN_COUNTRIES.find((c) => c.code === countryCode);
  return stored === normalize(countryCode) || (country !== undefined && stored === normalize(country.name));
}

export function matchesSearch(course: { name: string; clubName: string; id: string }, query: string): boolean {
  const needle = normalize(query);
  if (!needle) {
    return true;
  }
  return (
    course.name.toLowerCase().includes(needle) ||
    course.clubName.toLowerCase().includes(needle) ||
    course.id.toLowerCase().includes(needle)
  );
}

export function matchesAnalysisFilter(course: { analysisState: string | null }, filter: AnalysisFilter): boolean {
  if (filter === 'all') {
    return true;
  }
  const analysed = course.analysisState === 'analysed';
  return filter === 'analysed' ? analysed : !analysed;
}

/** Every ticked kind must be present: the checkboxes narrow, they do not widen. */
export function matchesOsmFeatures(
  course: { osmFeatures: OsmFeatureCounts },
  kinds: OsmFeatureKind[],
): boolean {
  return kinds.every((kind) => (course.osmFeatures?.[kind] ?? 0) > 0);
}

export function filterCourses<T extends FilterableCourse>(courses: T[], filters: CourseListFilters): T[] {
  return courses.filter(
    (course) =>
      matchesCountry(course, filters.countryCode) &&
      matchesSearch(course, filters.query) &&
      matchesAnalysisFilter(course, filters.analysisFilter) &&
      matchesOsmFeatures(course, filters.osmFeatures),
  );
}

export type Page<T> = {
  items: T[];
  /** Clamped to the available range, so a stale page number in the URL still renders something. */
  page: number;
  pageCount: number;
  /** 1-based inclusive range of the items on this page, for "showing 1–100 of 240". */
  from: number;
  to: number;
  total: number;
};

export function pageOf<T>(items: T[], page: number, pageSize = COURSE_LIST_PAGE_SIZE): Page<T> {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(Math.max(1, Math.floor(page) || 1), pageCount);
  const start = (current - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  return {
    items: pageItems,
    page: current,
    pageCount,
    from: pageItems.length === 0 ? 0 : start + 1,
    to: start + pageItems.length,
    total: items.length,
  };
}

type RawParam = string | string[] | undefined;

const first = (value: RawParam): string | undefined => (Array.isArray(value) ? value[0] : value);

const ANALYSIS_FILTERS: AnalysisFilter[] = ['all', 'analysed', 'notAnalysed'];

/**
 * The URL is the list's memory: search text, country, status filter and page all survive a
 * reload and can be shared with another admin. Anything unrecognised falls back to the default
 * rather than showing an empty list the admin cannot explain.
 */
export function parseCourseListParams(params: Record<string, RawParam>): CourseListFilters {
  const country = first(params.country);
  const status = first(params.status);
  const page = Number.parseInt(first(params.page) ?? '', 10);

  const knownCountry =
    country === ALL_COUNTRIES || COURSE_ADMIN_COUNTRIES.some((c) => c.code === country);

  return {
    query: first(params.q) ?? DEFAULT_COURSE_LIST_FILTERS.query,
    countryCode: knownCountry ? (country as string) : DEFAULT_COURSE_LIST_FILTERS.countryCode,
    analysisFilter: ANALYSIS_FILTERS.includes(status as AnalysisFilter)
      ? (status as AnalysisFilter)
      : DEFAULT_COURSE_LIST_FILTERS.analysisFilter,
    osmFeatures: (first(params.osm) ?? '')
      .split(',')
      .filter((kind): kind is OsmFeatureKind => OSM_FEATURE_KINDS.includes(kind as OsmFeatureKind)),
    page: Number.isFinite(page) && page > 0 ? page : DEFAULT_COURSE_LIST_FILTERS.page,
  };
}

export function courseListParams(filters: CourseListFilters): Record<string, string> {
  return {
    q: filters.query,
    country: filters.countryCode,
    status: filters.analysisFilter,
    // Order-stable, so the same set of ticks always produces the same URL.
    osm: OSM_FEATURE_KINDS.filter((kind) => filters.osmFeatures.includes(kind)).join(','),
    page: String(filters.page),
  };
}
